import React, { useEffect, useId, useRef } from 'react';
import { ChevronDown, Minus, Plus, X } from 'lucide-react';
import './FilterPill.css';

/**
 * A capsule filter control with an anchored popover.
 *
 * Empty state:  [+ Label]
 * Active state: [Label | value ▾ ×]
 *
 * The popover content is passed as children and is rendered only while open.
 * Outside clicks and Escape close it via `onClose`.
 */
const FilterPill = ({
    label,
    value,
    isOpen,
    onOpen,
    onClose,
    onClear,
    count,
    leadingIcon = 'plus',
    popoverWidth,
    popoverAlign = 'left',
    children,
}) => {
    const rootRef = useRef(null);
    const popoverId = useId();
    const isActive = Boolean(value);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handlePointer = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                onClose();
            }
        };
        const handleKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', handlePointer);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handlePointer);
            document.removeEventListener('keydown', handleKey);
        };
    }, [isOpen, onClose]);

    const handleClear = (event) => {
        event.stopPropagation();
        onClear?.();
        if (isOpen) onClose();
    };

    const LeadingIcon = leadingIcon === 'minus' ? Minus : Plus;

    return (
        <div className="filter-pill-wrap" ref={rootRef}>
            <button
                type="button"
                className={`filter-pill${isActive ? ' is-active' : ''}${isOpen ? ' is-open' : ''}`}
                onClick={() => (isOpen ? onClose() : onOpen())}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-controls={isOpen ? popoverId : undefined}
            >
                {!isActive && <LeadingIcon size={14} className="filter-pill__icon" aria-hidden="true" />}
                <span className={`filter-pill__label${isActive ? ' is-muted' : ''}`}>{label}</span>
                {isActive && (
                    <>
                        <span className="filter-pill__divider" aria-hidden="true" />
                        <span className="filter-pill__value">{value}</span>
                        <ChevronDown size={13} className="filter-pill__icon filter-pill__chevron" aria-hidden="true" />
                    </>
                )}
                {typeof count === 'number' && count > 0 && (
                    <span className="filter-pill__count" aria-label={`${count} applied`}>{count}</span>
                )}
                {isActive && onClear && (
                    <span
                        role="button"
                        tabIndex={0}
                        className="filter-pill__clear"
                        aria-label={`Clear ${label}`}
                        title="Clear"
                        onClick={handleClear}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleClear(event);
                            }
                        }}
                    >
                        <X size={12} />
                    </span>
                )}
            </button>

            {isOpen && (
                <div
                    id={popoverId}
                    role="dialog"
                    aria-label={label}
                    className={`filter-popover${popoverAlign === 'right' ? ' is-right' : ''}`}
                    style={popoverWidth ? { width: popoverWidth } : undefined}
                >
                    {children}
                </div>
            )}
        </div>
    );
};

export default FilterPill;
